import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUp, ChevronRight, List } from 'lucide-react';
import { preparePolicyDocument } from '@/lib/policy-document';

interface PolicyDocumentProps {
  title: string;
  slug: string;
  html: string;
  /** ISO timestamp of the page's last edit; the fallback "last updated" date. */
  updatedAt?: string | null;
}

/** The two policies every site ships with; shown as cross-links under the body. */
const CORE_POLICIES = [
  { slug: 'privacy-policy', labelKey: 'policy.privacy' },
  { slug: 'terms-of-service', labelKey: 'policy.terms' },
] as const;

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * Layout for legal / policy pages (Privacy Policy, Terms of Service, and the
 * other policy documents a site carries). These are long, sectioned documents
 * people scan for one clause, so the page is built for that: a title band with
 * the date it was last updated, a contents list that follows the reader down
 * the page, anchored sections, and a readable measure.
 *
 * The body is the page's stored HTML; sections come from its <h2> headings.
 */
export default function PolicyDocument({ title, slug, html, updatedAt }: PolicyDocumentProps) {
  const { t } = useTranslation('pages');
  const doc = useMemo(() => preparePolicyDocument(html), [html]);
  const lastUpdated = doc.lastUpdated ?? formatDate(updatedAt);
  const hasContents = doc.sections.length >= 3;
  const [activeId, setActiveId] = useState<string | null>(null);

  // Mark the section being read in the contents list: the last heading that
  // has passed the top third of the screen. Computed from scroll position
  // rather than an IntersectionObserver, which misses headings on a jump
  // (an anchor click, Page Down) and leaves the wrong entry marked.
  useEffect(() => {
    if (!hasContents) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const line = window.innerHeight * 0.3;
      let current: string | null = null;
      for (const s of doc.sections) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top <= line) current = s.id;
      }
      setActiveId(current);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [doc.sections, hasContents]);

  const related = CORE_POLICIES.filter((p) => p.slug !== slug);

  const contents = (
    <ol className="space-y-1 text-sm">
      {doc.sections.map((s) => (
        <li key={s.id}>
          <a
            href={`#${s.id}`}
            aria-current={activeId === s.id ? 'location' : undefined}
            className={`block border-l-2 py-1.5 pl-3 leading-snug transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 ${
              activeId === s.id
                ? 'border-sage-700 font-medium text-sage-900'
                : 'border-line text-ink-soft hover:border-sage-400 hover:text-sage-800'
            }`}
          >
            {s.title}
          </a>
        </li>
      ))}
    </ol>
  );

  return (
    <main id="top">
      <header className="border-b border-sage-200 bg-sage-50">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
          <nav aria-label={t('breadcrumbs.label')}>
            <ol className="flex items-center gap-1.5 text-sm text-ink-soft">
              <li>
                <Link
                  to="/"
                  className="rounded transition-colors hover:text-sage-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500"
                >
                  {t('breadcrumbs.home')}
                </Link>
              </li>
              <li aria-hidden="true">
                <ChevronRight className="h-4 w-4" />
              </li>
              <li>
                <span className="font-medium text-ink" aria-current="page">
                  {title}
                </span>
              </li>
            </ol>
          </nav>
          <p className="mt-8 text-xs font-semibold uppercase tracking-wide text-sage-700">
            {t('policy.eyebrow')}
          </p>
          <h1 className="mt-2 font-display text-4xl font-bold text-sage-900 sm:text-5xl">{title}</h1>
          {lastUpdated && (
            <p className="mt-4 text-sm text-ink-soft">{t('lastUpdated', { date: lastUpdated })}</p>
          )}
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
        <div className={hasContents ? 'lg:grid lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-14' : ''}>
          {hasContents && (
            <>
              {/* Phones and tablets: contents fold away above the text. */}
              <details className="mb-8 rounded-xl border border-line bg-surface-soft lg:hidden">
                <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-semibold text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500">
                  <List className="h-4 w-4" aria-hidden="true" />
                  {t('policy.onThisPage')}
                </summary>
                <nav aria-label={t('policy.onThisPage')} className="px-4 pb-4">
                  {contents}
                </nav>
              </details>
              {/* Desktop: contents stay beside the text while it scrolls. */}
              <aside className="hidden lg:block">
                <nav
                  aria-label={t('policy.onThisPage')}
                  className="sticky top-28 max-h-[calc(100vh-9rem)] overflow-y-auto pr-2"
                >
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-soft">
                    {t('policy.onThisPage')}
                  </p>
                  {contents}
                </nav>
              </aside>
            </>
          )}

          <div className="min-w-0">
            <article
              className="prose max-w-[70ch] dark:prose-invert prose-headings:font-display prose-headings:text-sage-900 prose-h2:mt-12 prose-h2:scroll-mt-28 prose-h2:border-t prose-h2:border-line prose-h2:pt-8 prose-h2:text-2xl prose-h2:first:mt-0 prose-h2:first:border-t-0 prose-h2:first:pt-0 prose-h3:text-lg prose-p:leading-relaxed prose-a:text-sage-700 prose-li:leading-relaxed prose-img:rounded-lg"
              dangerouslySetInnerHTML={{ __html: doc.html }}
            />

            <footer className="mt-14 max-w-[70ch] border-t border-line pt-8">
              {related.length > 0 && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
                    {t('policy.related')}
                  </p>
                  <ul className="mt-3 flex flex-wrap gap-3">
                    {related.map((p) => (
                      <li key={p.slug}>
                        <Link
                          to={`/page/${p.slug}`}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-sage-300 px-4 py-2 text-sm font-medium text-sage-800 transition-colors hover:bg-sage-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500"
                        >
                          {t(p.labelKey)}
                          <ChevronRight className="h-4 w-4" aria-hidden="true" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <a
                href="#top"
                className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium text-sage-700 hover:text-sage-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500"
              >
                <ArrowUp className="h-4 w-4" aria-hidden="true" />
                {t('policy.backToTop')}
              </a>
            </footer>
          </div>
        </div>
      </div>
    </main>
  );
}
